import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const SearchBar = ({ onSearch, placeholder = "Search API documentation..." }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      onSearch(searchTerm);
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, onSearch]);

  const handleClear = () => {
    setSearchTerm('');
    onSearch('');
  };

  const handleFocus = () => {
    setIsExpanded(true);
  };

  const handleBlur = () => {
    if (!searchTerm) {
      setIsExpanded(false);
    }
  };

  return (
    <div className="relative">
      <div className={`relative transition-all duration-200 ${
        isExpanded ? 'w-full max-w-md' : 'w-64'
      }`}>
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon name="Search" size={16} className="text-muted-foreground" />
        </div>
        
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
        />
        
        {searchTerm && (
          <button
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={16} />
          </button>
        )}
      </div>
      {/* Search Suggestions/Results */}
      {searchTerm && isExpanded && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-elevation-3 z-50 max-h-64 overflow-y-auto">
          <div className="p-2">
            <div className="text-xs text-muted-foreground px-3 py-2">
              Search results for "{searchTerm}"
            </div>
            
            {/* Mock search results */}
            {[
              { title: 'Send Message', section: 'Messaging API', type: 'endpoint' },
              { title: 'Authentication', section: 'Getting Started', type: 'guide' },
              { title: 'Webhook Events', section: 'Webhooks', type: 'reference' },
              { title: 'Rate Limits', section: 'Getting Started', type: 'guide' }
            ]?.filter(item => 
              item?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
              item?.section?.toLowerCase()?.includes(searchTerm?.toLowerCase())
            )?.slice(0, 5)?.map((result, index) => (
              <button
                key={index}
                className="w-full text-left px-3 py-2 hover:bg-muted rounded-md transition-colors"
                onClick={() => {
                  setSearchTerm('');
                  setIsExpanded(false);
                }}
              >
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={result?.type === 'endpoint' ? 'Zap' : result?.type === 'guide' ? 'Book' : 'FileText'} 
                    size={14} 
                    className="text-muted-foreground" 
                  />
                  <div>
                    <div className="text-sm font-medium text-foreground">{result?.title}</div>
                    <div className="text-xs text-muted-foreground">{result?.section}</div>
                  </div>
                </div>
              </button>
            ))}
            
            {searchTerm && ![
              { title: 'Send Message', section: 'Messaging API', type: 'endpoint' },
              { title: 'Authentication', section: 'Getting Started', type: 'guide' },
              { title: 'Webhook Events', section: 'Webhooks', type: 'reference' },
              { title: 'Rate Limits', section: 'Getting Started', type: 'guide' }
            ]?.some(item => 
              item?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
              item?.section?.toLowerCase()?.includes(searchTerm?.toLowerCase())
            ) && (
              <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                No results found for "{searchTerm}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;